import { createTheme } from '@mui/material/styles';
import '@mui/x-date-pickers/themeAugmentation';

import checkIcon from '@user/assets/images/common/check.svg';
import checkCheckedIcon from '@user/assets/images/common/check-checked.svg';
import radioOff from '@user/assets/images/common/radio-off.svg';
import radioOn from '@user/assets/images/common/radio-on.svg';
import selectArrow from '@user/assets/images/common/select-down.svg';
import calendarIcon from '@user/assets/images/common/calendar-secondary.svg';
import paginationPrev from '@user/assets/images/common/chevron-left.svg';
import paginationNext from '@user/assets/images/common/chevron-right.svg';
import paginationPrevDisabled from '@user/assets/images/common/chevron-left-disabled.svg';
import paginationNextDisabled from '@user/assets/images/common/chevron-right-disabled.svg';

// ── 커스텀 variant 타입 확장 (TypeScript) ─────────────────────────────────────
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    additional: true; // 커스텀 variant 이름
  }
}

// ── 커스텀 아이콘 컴포넌트 ────────────────────────────────────────────────────

const SelectIcon = ({ ownerState, ...rest }: any) => (
  <img
    {...rest}
    src={selectArrow}
    alt=""
    style={{ width: 16, height: 16, pointerEvents: 'none' }}
  />
);

const CalendarIcon = ({ ownerState, ...rest }: any) => (
  <img
    {...rest}
    src={calendarIcon}
    alt=""
    style={{ width: 20, height: 20, pointerEvents: 'none' }}
  />
);

const PaginationPrevIcon = ({ ownerState, ...rest }: any) => (
  <img
    src={ownerState?.disabled ? paginationPrevDisabled : paginationPrev}
    alt=""
    style={{ width: 16, height: 16, pointerEvents: 'none' }}
  />
);

const PaginationNextIcon = ({ ownerState, ...rest }: any) => (
  <img
    src={ownerState?.disabled ? paginationNextDisabled : paginationNext}
    alt=""
    style={{ width: 16, height: 16, pointerEvents: 'none' }}
  />
);

const theme = createTheme({
  palette: {
    primary: {
      main: '#051766',
      contrastText: '#fff',
    },
  },
  components: {

    // ── Tooltip ──────────────────────────────────────────────────────────────
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
      },
      styleOverrides: {
        popper: {
          pointerEvents: 'none',
        },
        tooltip: {
          backgroundColor: '#252525',
          color: '#fff',
          fontSize: '12px',
        },
      },
    },

    // ── Checkbox ─────────────────────────────────────────────────────────────
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          width: 16,
          height: 16,
          padding: 0,
          display: 'inline-block',
          borderRadius: '2px',
          backgroundImage: `url("${checkIcon}")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '16px',
          '& .MuiSvgIcon-root': { display: 'none' },
          '&.Mui-checked': {
            backgroundImage: `url("${checkCheckedIcon}")`,
          },
          // 체크박스 자체에 마우스 오버 시
          '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: '0 0 0 2px #fff, 0 0 0 3px #051766',
            borderRadius: '1px',
          },
          // 키보드 포커스 시
          '&.Mui-focusVisible': {
            boxShadow: '0 0 0 2px #fff, 0 0 0 3px #051766',
            borderRadius: '1px',
          },
        },
      },
    },

    // ── Radio ─────────────────────────────────────────────────────────────────
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          width: 20,
          height: 20,
          padding: 0,
          display: 'inline-block',
          borderRadius: '8px',
          backgroundImage: `url("${radioOff}")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '20px',
          '& .MuiSvgIcon-root': { display: 'none' },
          '&.Mui-checked': {
            backgroundImage: `url("${radioOn}")`,
          },
          // 라디오 자체 hover
          '&:hover': {
            boxShadow: '0 0 0 2px #fff, 0 0 0 3px #051766',
            borderRadius: '50%',
          },
          // 키보드 포커스
          '&.Mui-focusVisible': {
            boxShadow: '0 0 0 2px #fff, 0 0 0 3px #051766',
            borderRadius: '50%',
          },
        },
      },
    },

    // ── FormControlLabel ──────────────────────────────────────────────────────
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '&:hover .MuiCheckbox-root': {
            boxShadow: '0 0 0 2px #fff, 0 0 0 3px #051766',
            borderRadius: '1px',
          },
          '&:hover .MuiRadio-root': {
            boxShadow: '0 0 0 2px #fff, 0 0 0 3px #051766',
            borderRadius: '50%',
          },
        },
      },
    },

    // ── Button ────────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          transition: 'background-color 0.15s ease, opacity 0.15s ease, border-color 0.15s ease',
          '&.Mui-focusVisible': {
            boxShadow: 'none',
          },
        },

        // ── contained ──────────────────────────────────────────────────────
        contained: {
          // 기본
          backgroundColor: '#57bbeb',
          color: '#051766',
          borderRadius: '30px',
          padding: '4px 12px',
          // hover
          '&:hover': {
            background: 'linear-gradient(0deg, #0517661a 0% 100%), #57bbeb',
          },
          // active (클릭)
          '&:active': {
            background: 'linear-gradient(0deg, #05176626 0% 100%), #57bbeb',
            scale: '.98',
          },
          // focus
          '&.Mui-focusVisible': {
            outline: '1px solid #051766',
            outlineOffset: '2px',
          },
          // disabled
          '&.Mui-disabled': {
            backgroundColor: '##d9d9d9',
            color: '#a4a4a4',
          },
        },

        // ── outlined ───────────────────────────────────────────────────────
        outlined: {
          // 기본
          borderColor: '#051766',
          color: '#051766',
          backgroundColor: '#fff',
          borderRadius: '30px',
          padding: '4px 12px',
          // hover
          '&:hover': {
            background: 'linear-gradient(0deg, #0517660a 0% 100%), #fff',
          },
          // active
          '&:active': {
            background: 'linear-gradient(0deg, #0517660f 0% 100%), #fff',
            scale: '.98',
          },
          // focus
          '&.Mui-focusVisible': {
            outline: '1px solid #051766',
            outlineOffset: '2px',
            backgroundColor: '#fff',
          },
          // disabled
          '&.Mui-disabled': {
            background: '#f7f7f7',
            borderColor: '#a4a4a4',
            color: '#a4a4a4',
          },
        },

        // ── addtional ───────────────────────────────────────────
      },
      variants: [
        {
          props: { variant: 'additional' },
          style: {
            // 기본
            backgroundColor: '#ddf1fb',
            borderRadius: '30px',
            color: '#051766',
            padding: '4px 12px',
            
            // hover
            '&:hover': {
              background: 'linear-gradient(0deg, #0517660a 0% 100%), #ddf1fb',
            },
            // active
            '&:active': {
              background: 'linear-gradient(0deg, #05176612 0% 100%), #ddf1fb',
              scale: '.98',
            },
            // focus
            '&.Mui-focusVisible': {
              outline: '1px solid #051766',
              outlineOffset: '2px',
            },
            // disabled
            '&.Mui-disabled': {
              backgroundColor: '#d9d9d9',
              color: '#a4a4a4',
            },
          },
        },
      ],
    },

    // ── IconButton ────────────────────────────────────────────────────────────
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'transparent' },
          '&:active': { backgroundColor: 'transparent' },
          '&:focus': { backgroundColor: 'transparent' },
          '&.Mui-focusVisible': { backgroundColor: 'transparent' },
          '& .MuiTouchRipple-root': { display: 'none' },
        },
      },
    },

    // ── ButtonBase (모든 버튼 계열 공통 베이스) ────────────────────────────────
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },

    // ── Select ────────────────────────────────────────────────────────────────
    MuiSelect: {
      defaultProps: {
        IconComponent: SelectIcon,
      },
      styleOverrides: {
        root: {
          // focus 시 테두리 두꺼워지는 효과 제거
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          // hover 시
          '&:hover': {
            boxShadow: 'inset #5e5e5e 0 0 0 1px !important',
          },
          // 선택(열린) 상태 — Mui-focused 유지됨
          '&.Mui-focused': {
            boxShadow: 'inset #5e5e5e 0 0 0 1px !important',
          },
        },
      },
    },

    // ── OutlinedInput (TextField, Select 등 공통) ─────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // hover 시 테두리 두꺼워지는 효과 제거
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          // focus 시 테두리 두꺼워지는 효과 제거
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          // hover 시
          '&:hover': {
            boxShadow: 'inset #5e5e5e 0 0 0 1px !important',
          },
          // focus 시
          '&.Mui-focused': {
            boxShadow: 'inset #5e5e5e 0 0 0 1px !important',
          },
          // active 시
          '&:active': {
            boxShadow: 'inset #5e5e5e 0 0 0 1px !important',
          },
          // disabled 시 box-shadow 제거
          '&.Mui-disabled': {
            boxShadow: 'none !important',
          },
        },
      },
    },

    // ── Tab / Tabs ────────────────────────────────────────────────────────────
    MuiTab: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-focusVisible': { backgroundColor: 'transparent' },
        },
      },
    },

    // ── MenuItem ──────────────────────────────────────────────────────────────
    MuiMenuItem: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          '&.Mui-focusVisible': { backgroundColor: 'transparent' },
        },
      },
    },

    // ── ListItemButton ────────────────────────────────────────────────────────
    MuiListItemButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          '&.Mui-focusVisible': { backgroundColor: 'transparent' },
        },
      },
    },

    // ── TableRow ──────────────────────────────────────────────────────────────
    MuiTableRow: {
      styleOverrides: {
        root: {
          // 기본 hover 배경색 제거 (필요 시 직접 지정)
          '&:hover': { backgroundColor: 'transparent' },
          // MUI Table의 hover prop 사용 시 적용되는 스타일도 제거
          '&.MuiTableRow-hover:hover': { backgroundColor: 'transparent' },
        },
      },
    },

    // ── DatePicker ────────────────────────────────────────────────────────────
    MuiDatePicker: {
      defaultProps: {
        desktopModeMediaQuery: '@media (min-width: 0px)',
        slots: {
          openPickerIcon: CalendarIcon,
        },
      },
    },

    // ── DateTimePicker ────────────────────────────────────────────────────────
    MuiDateTimePicker: {
      defaultProps: {
        desktopModeMediaQuery: '@media (min-width: 0px)',
        slots: {
          openPickerIcon: CalendarIcon,
        },
      },
    },

    // ── CssBaseline ───────────────────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        'input::placeholder': {
          color: '#5e5e5e !important',
          opacity: '1 !important',
        },
        'textarea::placeholder': {
          color: '#5e5e5e !important',
          opacity: '1 !important',
        },
      },
    },

    // ── Pagination ────────────────────────────────────────────────────────────
    MuiPagination: {
      defaultProps: {
        shape: 'rounded',
        color: 'primary',
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '& .MuiTouchRipple-root': { display: 'none' },
          // 이전/다음 버튼 공통 — 기본 SVG 숨김
          '&.MuiPaginationItem-firstLast': {
            width: '28px',
            height: '28px',
            borderColor: '#051766',
            borderRadius: '50%',
            '& svg': { display: 'none !important' },
            '&::after': {
              content: '""',
              display: 'block',
              width: 28,
              height: 28,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: '16px',
            },
            '&.Mui-disabled': {
              opacity: 1,
            },
          },

          // 첫 페이지 버튼 (aria-label="Go to first page")
          '&.MuiPaginationItem-firstLast[aria-label="Go to first page"]::after': {
            backgroundImage: `url("${paginationPrev}")`,
          },

          // 마지막 페이지 버튼 (aria-label="Go to last page")
          '&.MuiPaginationItem-firstLast[aria-label="Go to last page"]::after': {
            backgroundImage: `url("${paginationNext}")`,
          },

          // 비활성 시
          '&.MuiPaginationItem-firstLast.Mui-disabled[aria-label="Go to first page"]::after': {
            backgroundImage: `url("${paginationPrevDisabled}")`,
          },
          '&.MuiPaginationItem-firstLast.Mui-disabled[aria-label="Go to last page"]::after': {
            backgroundImage: `url("${paginationNextDisabled}")`,
          },

          // 기본
          color: '#051766',
          borderColor: '#051766',
          fontSize: '13px',
          minWidth: '28px',
          height: '28px',
          // hover
          '&:hover': {
            backgroundColor: '#f4f4f4',
          },
          // 선택된 페이지
          '&.Mui-selected': {
            backgroundColor: '#ddf1fb',
            color: '#051766',
            fontWeight: '700 !important',
            cursor: 'default',
            pointerEvents: 'none', 
            '&.Mui-focusVisible': {
              backgroundColor: '#ddf1fb',
            outline: 'none',
            },
          },
          // focus
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent',
            outline: '1px solid #051766',
            outlineOffset: '0',
          },
          // 이전/다음 화살표 버튼
          '&.MuiPaginationItem-previousNext': {
            borderColor: '#051766',
          },
        },
      },
    },

  },
});

export default theme;
